import { useEffect, useState, useCallback, useMemo } from "react";
import { useFlowsTemplates } from "../../../../stores/flowsTemplatesStore";
import { useFlowsSavedDocuments } from "../../../../stores/flowsSavedDocumentsStore";
import APIs from "../../../../application/APIs";
import { useSnackbar } from "../../../../context/Snackbar/SnackbarContext";
import { SnackbarType } from "../../../../context/Snackbar/Types";
import { ClientData, RelatedData, PendingDocument } from "../types";

export interface CustomVariablePlaceholder {
  key: string;
  label: string;
}

export interface UseGeneratedDocumentsTabReturn {
  // Form state
  selectedTemplateId: string;
  setSelectedTemplateId: (id: string) => void;
  selectedClientId: string;
  setSelectedClientId: (id: string) => void;
  selectedBankId: string;
  setSelectedBankId: (id: string) => void;
  effectiveDate: string;
  setEffectiveDate: (date: string) => void;

  // Custom variable state
  customVariablePlaceholders: CustomVariablePlaceholder[];
  customVariableValues: Record<string, string>;
  setCustomVariableValue: (key: string, value: string) => void;

  // Data
  clients: ClientData[];
  clientsLoading: boolean;
  relatedData: RelatedData | null;
  relatedDataLoading: boolean;
  pendingDocuments: PendingDocument[];
  generatingAll: boolean;

  // Computed
  templatesWithPlaceholders: ReturnType<typeof useFlowsTemplates>["templates"];
  requiresBankAccount: boolean;
  canAddToPending: boolean;
  hasPendingDocs: boolean;

  // Actions
  handleAddToPending: () => void;
  handleRemovePending: (id: string) => void;
  handleDownload: (docId: string, fileName: string) => Promise<void>;
  handleGenerateOne: (doc: PendingDocument, autoDownload?: boolean) => Promise<void>;
  handleSaveDocument: (doc: PendingDocument) => Promise<void>;
  handleGenerateAll: () => Promise<void>;
  handlePreview: () => Promise<void>;
}

export function useGeneratedDocumentsTab(): UseGeneratedDocumentsTabReturn {
  const {
    templates,
    fetchTemplates,
  } = useFlowsTemplates();

  const {
    saveGeneratedDocument,
  } = useFlowsSavedDocuments();

  const { showSnackbar } = useSnackbar();

  // Form state
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [selectedBankId, setSelectedBankId] = useState<string>("");
  const [effectiveDate, setEffectiveDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );

  // Data state
  const [clients, setClients] = useState<ClientData[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [relatedData, setRelatedData] = useState<RelatedData | null>(null);
  const [relatedDataLoading, setRelatedDataLoading] = useState(false);

  // Pending documents
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocument[]>([]);
  const [generatingAll, setGeneratingAll] = useState(false);

  // Custom variable values
  const [customVariableValues, setCustomVariableValues] = useState<Record<string, string>>({});

  const loadClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const response = await APIs.getFlowsAutomationsClients();
      setClients(response.clients || []);
      if (!response.clients || response.clients.length === 0) {
        showSnackbar({ title: "Info", message: "No clients found in database", type: SnackbarType.warning });
      }
    } catch (err) {
      console.error("Failed to load clients:", err);
      showSnackbar({ title: "Error", message: "Failed to load clients from MongoDB", type: SnackbarType.error });
    } finally {
      setClientsLoading(false);
    }
  }, [showSnackbar]);

  const loadClientRelatedData = useCallback(async (clientId: string) => {
    setRelatedDataLoading(true);
    try {
      const response = await APIs.getClientRelatedData(clientId);
      setRelatedData(response.related_data as RelatedData);
      setSelectedBankId("");
    } catch (err) {
      console.error("Failed to load client data:", err);
    } finally {
      setRelatedDataLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    loadClients();
  }, [fetchTemplates, loadClients]);

  useEffect(() => {
    if (selectedClientId) {
      loadClientRelatedData(selectedClientId);
    } else {
      setRelatedData(null);
    }
  }, [selectedClientId, loadClientRelatedData]);

  // Reset custom variable values when template changes
  useEffect(() => {
    setCustomVariableValues({});
  }, [selectedTemplateId]);

  const setCustomVariableValue = useCallback((key: string, value: string) => {
    setCustomVariableValues(prev => ({ ...prev, [key]: value }));
  }, []);

  // Compute custom variable placeholders from the selected template
  const customVariablePlaceholders = useMemo((): CustomVariablePlaceholder[] => {
    if (!selectedTemplateId) return [];
    const template = templates.find(t => t.identifier === selectedTemplateId);
    if (!template?.field_mappings?.placeholders) return [];

    const placeholders = template.field_mappings.placeholders as Array<{
      type?: string;
      db_column?: string;
      custom_variable_name?: string;
    }>;

    const result: CustomVariablePlaceholder[] = [];
    const seen = new Set<string>();

    for (const p of placeholders) {
      if (p.type === 'custom_variable' && p.custom_variable_name) {
        if (!seen.has(p.custom_variable_name)) {
          seen.add(p.custom_variable_name);
          result.push({ key: p.custom_variable_name, label: p.custom_variable_name });
        }
      } else if (p.type === 'placeholder' && p.db_column?.startsWith('TRANSACTIONS.')) {
        if (!seen.has(p.db_column)) {
          seen.add(p.db_column);
          const fieldName = p.db_column.split('.')[1] || p.db_column;
          const label = fieldName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          result.push({ key: p.db_column, label });
        }
      }
    }
    return result;
  }, [selectedTemplateId, templates]);

  const getFilledValues = useCallback(() => {
    if (!relatedData || !selectedTemplateId) return {};

    const template = templates.find(t => t.identifier === selectedTemplateId);
    if (!template?.field_mappings?.placeholders) return {};

    const placeholders = template.field_mappings.placeholders as Array<{ db_column: string }>;
    const values: Record<string, string> = {};

    const selectedBank = selectedBankId
      ? relatedData.bank_accounts.find(b => b.id === selectedBankId)
      : relatedData.bank_accounts[0];

    placeholders.forEach(p => {
      const [collection, field] = p.db_column.split(".");
      let value = "";

      if (collection === "CLIENTS" && relatedData.client) {
        value = String(relatedData.client[field] || "");
      } else if (collection === "COMPANIES" && relatedData.companies?.length > 0) {
        value = String(relatedData.companies[0][field] || "");
      } else if (collection === "BANK_ACCOUNTS" && selectedBank) {
        value = String((selectedBank as Record<string, unknown>)[field] || "");
      } else if (collection === "portfolio_data" && (relatedData as Record<string, unknown>).portfolio_data) {
        const portfolioDocs = (relatedData as Record<string, unknown>).portfolio_data as Record<string, unknown>[];
        if (portfolioDocs.length > 0) {
          value = String(portfolioDocs[0][field] || "");
        }
      }

      if (value) {
        values[p.db_column] = value;
      }
    });

    // Merge custom variable values
    Object.entries(customVariableValues).forEach(([key, value]) => {
      if (value) {
        values[key] = value;
      }
    });

    return values;
  }, [relatedData, selectedTemplateId, selectedBankId, templates, customVariableValues]);

  const handleAddToPending = useCallback(() => {
    if (!selectedTemplateId || !selectedClientId) {
      showSnackbar({ title: "Warning", message: "Please select a template and client", type: SnackbarType.warning });
      return;
    }

    const template = templates.find(t => t.identifier === selectedTemplateId);
    const client = clients.find(c => c.identifier === selectedClientId);
    const bank = selectedBankId ? relatedData?.bank_accounts.find(b => b.id === selectedBankId) : null;

    if (!template || !client) return;

    const filledValues = getFilledValues();
    const hasCustomValues = Object.values(customVariableValues).some(v => v.trim());
    if (Object.keys(filledValues).length === 0 && !hasCustomValues) {
      showSnackbar({ title: "Warning", message: "No values to fill. Make sure the template has placeholders.", type: SnackbarType.warning });
      return;
    }

    const pendingDoc: PendingDocument = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      templateId: template.identifier,
      templateName: template.name,
      clientId: client.identifier,
      clientName: `${client.first_name} ${client.last_name}`,
      bankId: bank?.id,
      bankName: bank ? `${bank.Bank} ****${bank.Bank_Account_Number?.slice(-4) || ""}` : undefined,
      effectiveDate,
      filledValues,
      placeholders: template.field_mappings?.placeholders || [],
      status: "pending",
    };

    setPendingDocuments(prev => [...prev, pendingDoc]);

    setSelectedClientId("");
    setSelectedBankId("");
    setRelatedData(null);

    showSnackbar({ title: "Added", message: "Document added to pending list", type: SnackbarType.success });
  }, [selectedTemplateId, selectedClientId, selectedBankId, templates, clients, relatedData, effectiveDate, getFilledValues, customVariableValues, showSnackbar]);

  const handleRemovePending = useCallback((id: string) => {
    setPendingDocuments(prev => prev.filter(d => d.id !== id));
  }, []);

  const handleDownload = useCallback(async (docId: string, fileName: string) => {
    try {
      const blob = await APIs.downloadGeneratedDocument(docId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
      showSnackbar({ title: "Error", message: "Failed to download document", type: SnackbarType.error });
    }
  }, [showSnackbar]);

  const handleGenerateOne = useCallback(async (doc: PendingDocument, autoDownload = true) => {
    setPendingDocuments(prev =>
      prev.map(d => d.id === doc.id ? { ...d, status: "generating" as const } : d)
    );

    try {
      const documentName = `${doc.templateName} - ${doc.clientName} - ${doc.effectiveDate}`;

      const savedDoc = await saveGeneratedDocument({
        template_id: doc.templateId,
        name: documentName,
        client_id: doc.clientId,
        client_name: doc.clientName,
        field_values: doc.filledValues,
        placeholders: doc.placeholders,
      });

      if (savedDoc) {
        setPendingDocuments(prev =>
          prev.map(d => d.id === doc.id ? { ...d, status: "done" as const, generatedDocId: savedDoc.identifier } : d)
        );
        showSnackbar({ title: "Success", message: `Generated: ${documentName}`, type: SnackbarType.success });

        if (autoDownload) {
          await handleDownload(savedDoc.identifier, documentName);
        }
      } else {
        throw new Error("Failed to save");
      }
    } catch (err) {
      console.error("Generate error:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to generate document";
      setPendingDocuments(prev =>
        prev.map(d => d.id === doc.id ? { ...d, status: "error" as const, errorMessage } : d)
      );
      showSnackbar({ title: "Error", message: errorMessage, type: SnackbarType.error });
    }
  }, [saveGeneratedDocument, handleDownload, showSnackbar]);

  const handleSaveDocument = useCallback(async (doc: PendingDocument) => {
    await handleGenerateOne(doc, false);
    showSnackbar({
      title: "Saved",
      message: "Document saved! You can now use it in Flows under 'USE SAVED DOCUMENT'",
      type: SnackbarType.success
    });
  }, [handleGenerateOne, showSnackbar]);

  const handleGenerateAll = useCallback(async () => {
    const pendingDocs = pendingDocuments.filter(d => d.status === "pending");
    if (pendingDocs.length === 0) return;

    setGeneratingAll(true);

    for (const doc of pendingDocs) {
      await handleGenerateOne(doc);
    }

    setGeneratingAll(false);
    showSnackbar({ title: "Done", message: "All documents generated", type: SnackbarType.success });
  }, [pendingDocuments, handleGenerateOne, showSnackbar]);

  const handlePreview = useCallback(async () => {
    if (!selectedTemplateId) return;
    try {
      const blob = await APIs.previewTemplatePDF(selectedTemplateId);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      showSnackbar({ title: "Error", message: "Failed to preview template", type: SnackbarType.error });
    }
  }, [selectedTemplateId, showSnackbar]);

  const templatesWithPlaceholders = useMemo(() =>
    templates.filter(
      t => t.file_name && t.field_mappings?.placeholders && (t.field_mappings.placeholders as unknown[]).length > 0
    ),
    [templates]
  );

  const selectedTemplate = useMemo(() =>
    templates.find(t => t.identifier === selectedTemplateId),
    [templates, selectedTemplateId]
  );

  const requiresBankAccount = useMemo(() =>
    !!selectedTemplate?.field_mappings?.placeholders?.some(
      (p: { db_column: string }) => p.db_column.startsWith("BANK_ACCOUNTS.")
    ),
    [selectedTemplate]
  );

  const canAddToPending = !!selectedTemplateId && !!selectedClientId && !relatedDataLoading;
  const hasPendingDocs = pendingDocuments.filter(d => d.status === "pending").length > 0;

  return {
    selectedTemplateId,
    setSelectedTemplateId,
    selectedClientId,
    setSelectedClientId,
    selectedBankId,
    setSelectedBankId,
    effectiveDate,
    setEffectiveDate,
    customVariablePlaceholders,
    customVariableValues,
    setCustomVariableValue,
    clients,
    clientsLoading,
    relatedData,
    relatedDataLoading,
    pendingDocuments,
    generatingAll,
    templatesWithPlaceholders,
    requiresBankAccount,
    canAddToPending,
    hasPendingDocs,
    handleAddToPending,
    handleRemovePending,
    handleDownload,
    handleGenerateOne,
    handleSaveDocument,
    handleGenerateAll,
    handlePreview,
  };
}
